/** The product fields Viewed Product reads, which every page's query has. */
type ViewedProductSource = {
  id: string;
  title: string;
  images: { edges: readonly { node: { url: string } }[] };
  variants: {
    edges: readonly {
      node: {
        sku?: string | null;
        availableForSale: boolean;
        price: { amount: string };
        compareAtPrice?: { amount: string } | null;
      };
    }[];
  };
};

/** Klaviyo's own browse category, so robot and accessory views can flow apart. */
export type KlaviyoProductCategory = "Robot" | "Accessory";

/**
 * Calls Klaviyo's onsite script, which records an event only for a visitor it
 * can identify (a Klaviyo email click, or `identify` below).
 *
 * A page's first effects run before ConsentScripts injects the script, so
 * until `window.klaviyo` exists the call waits on `_klOnsite`, the queue
 * klaviyo.js empties when it loads. Declined marketing never loads it, so a
 * queued call is never sent.
 */
const callKlaviyo = (
  method: "track" | "identify",
  args: readonly unknown[],
  failure: string,
): void => {
  if (
    typeof window === "undefined" ||
    window.location.hostname !== "maticrobots.com"
  ) {
    return;
  }
  const { klaviyo } = window;
  if (klaviyo === undefined) {
    window._klOnsite = window._klOnsite ?? [];
    window._klOnsite.push([method, ...args]);
    return;
  }
  const call =
    method === "track"
      ? klaviyo.track(args[0] as string, args[1] as Record<string, unknown>)
      : klaviyo.identify(args[0] as { email: string });
  call.catch((error: unknown) => {
    console.error(`${failure}:`, error);
  });
};

const isStaffEmail = (email: string) =>
  email.trim().toLowerCase().endsWith("@maticrobots.com");

export const identifyKlaviyo = (email: string): void => {
  if (isStaffEmail(email)) return;
  callKlaviyo("identify", [{ email }], "Failed to identify Klaviyo profile");
};

/** Klaviyo's standard "Viewed Product", which browse abandonment reads. */
export const trackKlaviyoViewedProduct = (
  product: ViewedProductSource,
  category: KlaviyoProductCategory,
): void => {
  try {
    const variant = product.variants.edges[0]?.node;
    if (variant === undefined) return;
    const compareAt = variant.compareAtPrice?.amount;
    callKlaviyo(
      "track",
      [
        "Viewed Product",
        {
          ProductName: product.title,
          ProductID: product.id.split("/").pop() ?? product.id,
          SKU: variant.sku ?? "",
          Categories: [category],
          ImageURL: product.images.edges[0]?.node.url ?? "",
          URL: `https://maticrobots.com${window.location.pathname}`,
          Brand: "Matic Robots",
          Price: Number(variant.price.amount),
          ...(compareAt === undefined
            ? {}
            : { CompareAtPrice: Number(compareAt) }),
          Available: variant.availableForSale,
        },
      ],
      "Failed to send Klaviyo Viewed Product",
    );
  } catch (error) {
    console.error("Failed to build Klaviyo Viewed Product:", error);
  }
};
